import type { AffixId, DieSides, MasterDie, Rarity } from '../../core/types';
import { clearSave, writeSave } from '../../core/storage';
import { AFFIXES, AFFIX_BY_ID } from '../../data/affixes';
import { RARITY_NAME, RARITY_ORDER } from '../../data/machines';
import { formatDamage, formatMult, formatNumber, formatPercent1, formatSeconds, rarityName } from '../format';
import { expectedThrow, factorLabel, hasEnemyHpAt, nextWall } from '../combatApi';
import { hasGateApi, hasMethod, onGameEvent, prepBoost } from '../gameApi';
import { materialList } from '../abyssApi';
import { spellLabel } from '../spellsApi';
import { onAutoThrowChange, readAutoThrow, setAutoThrow, supportsAutoThrow } from '../autoThrow';
import {
  DEFAULT_RENDER_PARAMS,
  PRESET_APERCU,
  RENDER_SLIDERS,
  type RenderParams,
  type RenderSlider,
} from '../../game3d/renderParams';
import { toast } from '../toast';
import { prepareImport } from '../screens/accalmie/saveImport';
import { publishTipsDebug, resetSeen } from '../tips';
import type { ScreenCtx } from '../types';
import {
  OVERRIDE_FIELDS,
  activeOverrides,
  applyOverrides,
  isTainted,
  markTainted,
  hasOverrideApi,
  loadStoredOverrides,
  resetOverrides,
  type BalanceOverrides,
  type OverrideKey,
} from './overrides';
import { clearDamageHistory, damageHistory, type HistoryEntry } from './history';
import { SPEEDS, currentSpeed, noFlight, onSpeedChange, setNoFlight, setSpeed } from './speed';
import { getTelemetry } from '../../telemetry/api';
import { flushGateEvents, pendingGateEvents } from '../../telemetry/events';
import { currentWorkerBase, formatCodeInput, redeemUnlockCode } from '../../telemetry/unlock';
import { getInstallId } from '../../telemetry/identity';
import { getEntitlement } from '../entitlement';
import { readAck } from '../entitlement/ack';
import { readEntitlement } from '../entitlement/store';
import { readConsent } from '../../telemetry/consent';
import { URL_OVERRIDE_KEY } from '../../telemetry/sender';
import { readPref, removePref, writePref } from '../prefs';
import { setTreeRevealAll, treeRevealAll } from '../treeReveal';

/**
 * Panneau de debug (onglet « Debug » des Notes de dev) — COMMENTS §7.
 * Sous-sections repliables : Outils rapides, Vue de combat, **Rendu 3D**
 * (v0.10.1), **Calculateur de dégâts** (v0.4.0), Équilibrage, Sauvegarde,
 * Historique des dégâts, Lancer automatique et Forgeur de dés.
 *
 * Toutes les API livrées par les autres agents (`setOverrides`, `forgeDie`,
 * `exportSave`, `Game.importSave`, `battle.setAutoThrow`) sont **détectées à
 * l'exécution** : une section dont l'API manque est masquée ou désactivée,
 * jamais en erreur.
 */

// ─────────────────────────────────────────────────────────────── Utilitaires

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Sections dépliées, mémorisées au niveau du module : le panneau est redessiné
 * après chaque action (Appliquer, Forger, Vider…) et un testeur ne doit pas
 * rouvrir sa section à chaque fois.
 */
const openSections = new Set<string>(['tools', 'balance']);

/**
 * Désabonnements du calculateur de dégâts. Le panneau est entièrement redessiné
 * à chaque action : les abonnements de la version précédente sont coupés au
 * début de `renderDebugTab`, et chaque écouteur se coupe aussi lui-même dès que
 * sa section quitte le document (fermeture de la modale).
 */
let calcUnsubs: (() => void)[] = [];

function section(title: string, id: string): HTMLDetailsElement {
  const details = el('details', 'f-panel f-details debug-section');
  details.dataset.section = id;
  details.open = openSections.has(id);
  const summary = el('summary', 'debug-summary', title);
  details.appendChild(summary);
  details.addEventListener('toggle', () => {
    if (details.open) openSections.add(id);
    else openSections.delete(id);
  });
  return details;
}

function button(label: string, className = 'f-btn f-btn--sm'): HTMLButtonElement {
  const b = el('button', className, label);
  b.type = 'button';
  return b;
}

/** Choix actif d'un groupe de boutons : filet d'or et `aria-pressed`. */
function markPressed(b: HTMLButtonElement, on: boolean): void {
  b.classList.toggle('f-btn--primary', on);
  b.setAttribute('aria-pressed', String(on));
}

/** Copie dans le presse-papiers, avec repli `select()` si l'API est refusée. */
function copyText(area: HTMLTextAreaElement): void {
  const text = area.value;
  const fallback = (): void => {
    area.focus();
    area.select();
    toast('Texte sélectionné : copiez-le à la main.');
  };
  const clip = navigator.clipboard as Clipboard | undefined;
  if (!clip || typeof clip.writeText !== 'function') {
    fallback();
    return;
  }
  clip.writeText(text).then(() => toast('Copié dans le presse-papiers.'), fallback);
}

/**
 * Bloc « zone de texte + Copier + Télécharger ». L'URL du Blob est recréée à
 * chaque mise à jour du contenu et l'ancienne révoquée (pas de fuite).
 */
function exportBlock(filename: string, initial: string): {
  root: HTMLElement;
  area: HTMLTextAreaElement;
  setValue(v: string): void;
} {
  const root = el('div', 'debug-export');
  const area = el('textarea', 'f-input f-textarea debug-textarea');
  area.rows = 6;
  area.spellcheck = false;
  area.setAttribute('data-role', `export-${filename}`);
  area.value = initial;

  const actions = el('div', 'debug-actions');
  const copy = button('Copier');
  copy.setAttribute('data-action', 'copy');
  copy.addEventListener('click', () => copyText(area));

  const link = el('a', 'f-btn f-btn--sm debug-download', 'Télécharger');
  link.download = filename;
  let url = '';
  const refreshUrl = (): void => {
    if (url) URL.revokeObjectURL(url);
    url = URL.createObjectURL(new Blob([area.value], { type: 'application/json' }));
    link.href = url;
  };
  refreshUrl();

  actions.append(copy, link);
  root.append(area, actions);
  return {
    root,
    area,
    setValue(v: string) {
      area.value = v;
      refreshUrl();
    },
  };
}

// ───────────────────────────────────────────────────── Section : équilibrage

function buildBalanceSection(ctx: ScreenCtx, rerender: () => void): HTMLElement {
  const { game } = ctx;
  const details = section('Équilibrage', 'balance');
  const available = hasOverrideApi(game);
  const current: BalanceOverrides = { ...loadStoredOverrides(), ...activeOverrides(game) };

  const form = el('div', 'debug-form');
  const inputs = new Map<OverrideKey, HTMLInputElement>();
  for (const field of OVERRIDE_FIELDS) {
    const row = el('label', 'debug-field');
    const name = el('span', 'debug-field-label', field.label);
    const input = el('input', 'f-input debug-input');
    input.type = 'number';
    input.step = String(field.step);
    if (field.min !== undefined) input.min = String(field.min);
    input.name = field.key;
    input.setAttribute('data-field', field.key);
    let effective = 0;
    try {
      effective = field.effective(game);
    } catch {
      effective = 0;
    }
    input.placeholder = Number.isFinite(effective) ? String(Number(effective.toFixed(3))) : '—';
    const value = current[field.key];
    if (typeof value === 'number') input.value = String(value);
    row.append(name, input);
    form.appendChild(row);
    inputs.set(field.key, input);
  }
  details.appendChild(form);

  const readForm = (): BalanceOverrides => {
    const out: BalanceOverrides = {};
    for (const [key, input] of inputs) {
      const raw = input.value.trim();
      if (raw === '') continue;
      const n = Number(raw);
      if (Number.isFinite(n)) out[key] = n;
    }
    return out;
  };

  const json = exportBlock('diceordie-balance.json', JSON.stringify(current, null, 2));
  const syncExport = (): void => json.setValue(JSON.stringify(readForm(), null, 2));
  for (const input of inputs.values()) input.addEventListener('input', syncExport);

  const actions = el('div', 'debug-actions');
  const apply = button('Appliquer', 'f-btn f-btn--primary f-btn--sm');
  apply.setAttribute('data-action', 'apply-overrides');
  apply.disabled = !available;
  if (!available) apply.title = 'API core absente (game.setOverrides)';
  apply.addEventListener('click', () => {
    const o = readForm();
    applyOverrides(game, o);
    toast(
      Object.keys(o).length
        ? `${Object.keys(o).length} surcharge(s) appliquée(s).`
        : 'Aucune surcharge : valeurs par défaut.',
    );
    rerender();
  });

  const reset = button('Réinitialiser');
  reset.setAttribute('data-action', 'reset-overrides');
  reset.addEventListener('click', () => {
    resetOverrides(game);
    toast("Surcharges d'équilibrage effacées.");
    rerender();
  });
  actions.append(apply, reset);

  const hint = el(
    'p',
    'debug-hint',
    available
      ? 'Champ vide = valeur du jeu (affichée en gris). Les surcharges sont conservées au rechargement, jamais sauvegardées dans la partie.'
      : "API core absente (game.setOverrides) : le formulaire sert uniquement à préparer le JSON à envoyer au développeur.",
  );

  details.append(actions, hint, json.root);
  return details;
}

// ────────────────────────────────────────────────────── Section : sauvegarde

function buildSaveSection(ctx: ScreenCtx): HTMLElement {
  const { game } = ctx;
  const details = section('Sauvegarde', 'save');

  const withExport = game as unknown as { exportSave?: () => string };
  const text =
    typeof withExport.exportSave === 'function'
      ? withExport.exportSave()
      : JSON.stringify(game.toSave(), null, 2);
  const json = exportBlock('diceordie-save.json', text);
  details.append(el('p', 'debug-hint', 'Exporter la partie courante :'), json.root);

  // ── Import ──────────────────────────────────────────────────────────
  details.appendChild(el('p', 'debug-hint', 'Importer une sauvegarde (le jeu recharge) :'));
  const area = el('textarea', 'f-input f-textarea debug-textarea');
  area.rows = 4;
  area.spellcheck = false;
  area.placeholder = 'Collez ici le JSON de sauvegarde…';
  area.setAttribute('data-role', 'import-save');
  details.appendChild(area);

  const file = el('input', 'debug-file');
  file.type = 'file';
  file.accept = 'application/json,.json';
  file.setAttribute('data-role', 'import-file');
  file.addEventListener('change', () => {
    const f = file.files?.[0];
    if (!f) return;
    void f.text().then((t) => {
      area.value = t;
      toast('Fichier chargé : cliquez sur « Importer ».');
    });
  });
  details.appendChild(file);

  const importBtn = button('Importer', 'f-btn f-btn--sm');
  importBtn.setAttribute('data-action', 'import-save');
  importBtn.addEventListener('click', () => {
    const raw = area.value.trim();
    if (!raw) {
      toast('Rien à importer.');
      return;
    }
    try {
      // Cœur partagé avec la modale « Sauvegarde » des Réglages (A96-3) ; ici,
      // la partie importée peut contenir n'importe quoi : elle est marquée debug
      // avant d'être écrite (classements, A87).
      const save = prepareImport(raw, { markDebug: true });
      writeSave(save);
      toast('Sauvegarde importée : rechargement…');
      window.setTimeout(() => window.location.reload(), 300);
    } catch (err) {
      toast(`Import impossible : ${(err as Error).message}`);
    }
  });
  const actions = el('div', 'debug-actions');
  actions.appendChild(importBtn);
  details.appendChild(actions);

  return details;
}

// ───────────────────────────────────────────── Section : historique des dégâts

function historyItem(entry: HistoryEntry): HTMLElement {
  const li = el('li', 'debug-history-item');
  const time = new Date(entry.at).toLocaleTimeString('fr-FR');
  const head = el('div', 'debug-history-head');
  head.append(
    el('span', 'debug-history-time', time),
    el('span', 'debug-history-phase', entry.phase),
    el('span', 'debug-history-enemy', entry.enemy),
  );
  li.appendChild(head);

  const dice = el('ul', 'debug-history-dice');
  for (const d of entry.dice) {
    const line = el('li');
    const parts = [`d${d.sides} face ${d.face}`, `→ ${formatDamage(d.damage)}`];
    if (d.absorbed) parts.push('absorbé');
    if (!d.isMaster) parts.push('basique');
    if (d.poison) parts.push(`poison ${formatDamage(d.poison)}/s`);
    line.textContent = parts.join(' · ');
    if (d.absorbed) line.classList.add('debug-history-absorbed');
    dice.appendChild(line);
  }
  li.appendChild(dice);

  const rows: string[] = [];
  if (entry.subtotal !== null) rows.push(`sous-total ${formatDamage(entry.subtotal)}`);
  if (entry.flatBonus !== null && entry.flatBonus > 0) {
    rows.push(`dégâts fixes +${formatDamage(entry.flatBonus)}`);
  }
  rows.push(entry.spells.length > 0 ? `sorts ${entry.spells.join(' + ')}` : 'sort —');
  // Facteurs nommés du cœur (Larbin, Premiers, ENFER…), inconnus compris.
  if (entry.factors.length > 0) rows.push(entry.factors.join(' · '));
  if (entry.crit === null) rows.push('critique ?');
  else if (entry.crit) {
    rows.push(`critique oui${entry.critMult ? ` ×${entry.critMult.toFixed(2).replace('.', ',')}` : ''}`);
  } else rows.push('critique non');
  rows.push(`total ${formatDamage(entry.total)}`);
  rows.push(`infligés ${formatDamage(entry.dealt)}`);
  if (entry.killed) rows.push('tué');

  const detail = el('p', 'debug-history-detail', rows.join(' · '));
  if (entry.killed) detail.classList.add('debug-history-kill');
  li.appendChild(detail);
  return li;
}

function buildHistorySection(rerender: () => void): HTMLElement {
  const details = section('Historique des dégâts', 'history');
  const entries = damageHistory();
  const actions = el('div', 'debug-actions');
  const clear = button('Vider');
  clear.setAttribute('data-action', 'clear-history');
  clear.addEventListener('click', () => {
    clearDamageHistory();
    rerender();
  });
  actions.appendChild(clear);
  details.appendChild(actions);

  if (entries.length === 0) {
    details.appendChild(
      el('p', 'debug-hint', 'Aucun lancer enregistré pour l’instant (50 derniers lancers).'),
    );
    return details;
  }
  const list = el('ul', 'debug-history');
  for (const entry of entries) list.appendChild(historyItem(entry));
  details.appendChild(list);
  return details;
}

// ─────────────────────────────────────────── Section : lancer automatique

/**
 * v0.5.0 : la case de debug pilote **la même option joueur** que l'Accalmie
 * (COMMENTS v0.4.1 §2, DECISIONS §E) — même préférence persistée, et les deux
 * cases se resynchronisent l'une l'autre.
 */
function buildAutoThrowSection(ctx: ScreenCtx): HTMLElement | null {
  const battle = ctx.battle;
  const details = section('Lancer automatique', 'autothrow');
  const label = el('label', 'debug-check');
  const input = el('input', 'f-check');
  input.type = 'checkbox';
  input.setAttribute('data-role', 'auto-throw');
  input.checked = readAutoThrow();
  input.addEventListener('change', () => {
    setAutoThrow(battle, input.checked);
    toast(input.checked ? 'Lancer automatique activé.' : 'Lancer automatique désactivé.');
  });
  const off = onAutoThrowChange((on) => {
    if (!details.isConnected) {
      off();
      return;
    }
    input.checked = on;
  });
  calcUnsubs.push(off);
  label.append(input, el('span', undefined, 'Lancer les dés automatiquement dès que possible'));
  details.appendChild(label);
  if (!supportsAutoThrow(battle)) {
    details.appendChild(
      el('p', 'debug-hint', 'Vue de combat sans setAutoThrow : l’option est mémorisée et s’appliquera dès qu’elle existera.'),
    );
  }
  return details;
}

// ───────────────────────────────────────────────── Section : forgeur de dés

/** Spécification d'un dé forgé ; niveau, Matériau et Enrichi (A94) sont ignorés par un cœur qui ne les connaît pas. */
interface ForgeDieSpec {
  sides: DieSides;
  rarity: Rarity;
  affixes: { id: AffixId; value: number }[];
  name?: string;
  /** Niveau du dé (≥ 1) ; absent : dé de campagne. */
  level?: number;
  material?: string;
  enriched?: boolean;
  /** A98 : dé des Corrupted GachaDice (le seul que la Forge accepte, Mult_Corrupted sur ses faces) ; un cœur plus ancien l'ignore. */
  corrupted?: boolean;
}

interface ForgeApi {
  forgeDie?: (spec: ForgeDieSpec) => MasterDie;
}

/** Plafond de niveau proposé au forgeur (le cœur borne à son tour : 1 à 60). */
const FORGE_LEVEL_MAX = 60;

const FORGE_ROWS = 4;
/** Formes proposées au forgeur : le d100 (A89) reste forgeable, seul moyen d'en obtenir depuis A93. */
const FORGE_SIDES: readonly DieSides[] = [4, 6, 12, 20, 100] as unknown as readonly DieSides[];

function buildForgeSection(ctx: ScreenCtx, rerender: () => void): HTMLElement | null {
  const { game } = ctx;
  const forge = game as unknown as ForgeApi;
  if (typeof forge.forgeDie !== 'function') return null;
  const details = section('Forgeur de dés', 'forge');

  const form = el('div', 'debug-form');

  const sidesRow = el('label', 'debug-field');
  sidesRow.append(el('span', 'debug-field-label', 'Faces'));
  const sidesSelect = el('select', 'f-select');
  sidesSelect.setAttribute('data-role', 'forge-sides');
  for (const s of FORGE_SIDES) {
    const opt = el('option', undefined, `d${s}`);
    opt.value = String(s);
    sidesSelect.appendChild(opt);
  }
  sidesRow.appendChild(sidesSelect);

  const rarityRow = el('label', 'debug-field');
  rarityRow.append(el('span', 'debug-field-label', 'Rareté'));
  const raritySelect = el('select', 'f-select');
  raritySelect.setAttribute('data-role', 'forge-rarity');
  for (const r of RARITY_ORDER) {
    const opt = el('option', undefined, RARITY_NAME[r] ?? r);
    opt.value = r;
    raritySelect.appendChild(opt);
  }
  rarityRow.appendChild(raritySelect);

  const nameRow = el('label', 'debug-field');
  nameRow.append(el('span', 'debug-field-label', 'Nom (optionnel)'));
  const nameInput = el('input', 'f-input debug-input');
  nameInput.type = 'text';
  nameInput.setAttribute('data-role', 'forge-name');
  nameInput.placeholder = 'Dé de test';
  nameRow.appendChild(nameInput);

  form.append(sidesRow, rarityRow, nameRow);

  // ── Mode infini (A94) : niveau, Matériau, Enrichi — seulement avec le cœur de la Forge ──
  let levelInput: HTMLInputElement | null = null;
  let materialSelect: HTMLSelectElement | null = null;
  let enrichedBox: HTMLInputElement | null = null;
  let corruptedBox: HTMLInputElement | null = null;
  if (hasMethod(game, 'hasForge')) {
    const levelRow = el('label', 'debug-field');
    levelRow.append(el('span', 'debug-field-label', 'Niveau (0 = aucun)'));
    levelInput = el('input', 'f-input debug-input debug-input--short');
    levelInput.type = 'number';
    levelInput.min = '0';
    levelInput.max = String(FORGE_LEVEL_MAX);
    levelInput.step = '1';
    levelInput.value = '0';
    levelInput.setAttribute('data-role', 'forge-level');
    levelRow.appendChild(levelInput);

    const materialRow = el('label', 'debug-field');
    materialRow.append(el('span', 'debug-field-label', 'Matériau'));
    materialSelect = el('select', 'f-select');
    materialSelect.setAttribute('data-role', 'forge-material');
    const noMaterial = el('option', undefined, '— aucun —');
    noMaterial.value = '';
    materialSelect.appendChild(noMaterial);
    for (const m of materialList()) {
      const opt = el('option', undefined, m.name);
      opt.value = m.id;
      materialSelect.appendChild(opt);
    }
    materialRow.appendChild(materialSelect);

    const enrichedRow = el('label', 'debug-field debug-field--check');
    enrichedBox = el('input', 'f-check');
    enrichedBox.type = 'checkbox';
    enrichedBox.setAttribute('data-role', 'forge-enriched');
    enrichedRow.append(enrichedBox, el('span', 'debug-field-label', 'Enrichi (affixes × 1,5)'));

    // A98 : sans la marque, le dé est un dé de campagne et la Forge le refuse.
    const corruptedRow = el('label', 'debug-field debug-field--check');
    corruptedBox = el('input', 'f-check');
    corruptedBox.type = 'checkbox';
    corruptedBox.setAttribute('data-role', 'forge-corrupted');
    corruptedRow.append(corruptedBox, el('span', 'debug-field-label', 'Corrupted (seul dé que la Forge accepte)'));

    form.append(levelRow, materialRow, enrichedRow, corruptedRow);
  }
  details.appendChild(form);

  // ── Lignes d'affixes ────────────────────────────────────────────────
  const rows: { select: HTMLSelectElement; value: HTMLInputElement; hint: HTMLElement }[] = [];
  const affixWrap = el('div', 'debug-affix-rows');
  for (let i = 0; i < FORGE_ROWS; i++) {
    const row = el('div', 'debug-affix-row');
    const select = el('select', 'f-select');
    select.setAttribute('data-role', `forge-affix-${i}`);
    const none = el('option', undefined, '— aucun —');
    none.value = '';
    select.appendChild(none);
    for (const a of AFFIXES) {
      const opt = el('option', undefined, `${a.name} (d${a.minSides}+)`);
      opt.value = a.id;
      select.appendChild(opt);
    }
    const value = el('input', 'f-input debug-input debug-input--short');
    value.type = 'number';
    value.step = '0.01';
    value.disabled = true;
    value.setAttribute('data-role', `forge-value-${i}`);
    const hint = el('span', 'debug-affix-hint', '—');

    const sync = (): void => {
      const def = AFFIX_BY_ID[select.value as AffixId];
      if (!def) {
        value.disabled = true;
        value.value = '';
        hint.textContent = '—';
        return;
      }
      value.disabled = false;
      value.step = String(def.step);
      value.min = String(def.min);
      value.max = String(def.max);
      if (!value.value) value.value = String(def.max);
      hint.textContent =
        def.unit === 'percent'
          ? `${Math.round(def.min * 100)} – ${Math.round(def.max * 100)} % (saisir ${def.min} – ${def.max})`
          : `${def.min} – ${def.max}`;
    };
    select.addEventListener('change', sync);
    row.append(select, value, hint);
    affixWrap.appendChild(row);
    rows.push({ select, value, hint });
  }
  details.appendChild(affixWrap);

  const readSpec = (): ForgeDieSpec => {
    const affixes: { id: AffixId; value: number }[] = [];
    for (const row of rows) {
      const id = row.select.value as AffixId;
      if (!id || !AFFIX_BY_ID[id]) continue;
      const n = Number(row.value.value);
      affixes.push({ id, value: Number.isFinite(n) ? n : AFFIX_BY_ID[id].min });
    }
    const name = nameInput.value.trim();
    // Niveau 0, aucun Matériau, non Enrichi : la spécification reste celle d'avant (dé de campagne).
    const level = Math.min(FORGE_LEVEL_MAX, Math.max(0, Math.floor(Number(levelInput?.value ?? 0)) || 0));
    const material = materialSelect?.value ?? '';
    return {
      sides: (FORGE_SIDES.find((n) => n === Number(sidesSelect.value)) ?? 4) as DieSides,
      rarity: raritySelect.value as Rarity,
      affixes,
      ...(name ? { name } : {}),
      ...(level > 0 ? { level } : {}),
      ...(material ? { material } : {}),
      ...(enrichedBox?.checked ? { enriched: true } : {}),
      ...(corruptedBox?.checked ? { corrupted: true } : {}),
    };
  };

  const doForge = (equip: boolean): void => {
    let die: MasterDie;
    try {
      markTainted(game);
      die = forge.forgeDie!(readSpec());
    } catch (err) {
      toast(`Forge impossible : ${(err as Error).message}`);
      return;
    }
    if (equip) {
      const free = game.state.equipped.indexOf(null);
      game.equipDie(die.id, free >= 0 ? free : 0);
    }
    toast(`${die.name} (d${die.sides} ${rarityName(die.rarity)}) ${equip ? 'forgé et équipé' : 'forgé'}.`);
    // Rafraîchit l'écran d'Accalmie derrière la modale (inventaire, stats).
    if (game.state.phase === 'accalmie') ctx.navigate('accalmie');
    rerender();
  };

  const actions = el('div', 'debug-actions');
  const forgeBtn = button('Forger', 'f-btn f-btn--primary f-btn--sm');
  forgeBtn.setAttribute('data-action', 'forge');
  forgeBtn.addEventListener('click', () => doForge(false));
  const forgeEquip = button('Forger et équiper', 'f-btn f-btn--sm');
  forgeEquip.setAttribute('data-action', 'forge-equip');
  forgeEquip.addEventListener('click', () => doForge(true));
  actions.append(forgeBtn, forgeEquip);
  details.appendChild(actions);

  return details;
}

// ──────────────────────────────────── Section : calculateur de dégâts (v0.4.0)

/** Une ligne « libellé → valeur » du calculateur. */
function calcRow(label: string, value: string, role: string): HTMLElement {
  const row = el('div', 'debug-calc-row');
  row.setAttribute('data-calc', role);
  row.append(el('dt', 'debug-calc-label', label), el('dd', 'debug-calc-value', value));
  return row;
}

/**
 * « Calculateur de dégâts » — COMMENTS v0.3.0 §1.2 / §7.
 *
 * Confronte les dégâts attendus d'un lancer (`game.expectedThrow()`) au mur de
 * PV de la prochaine Invasion au niveau courant, et en tire le verdict
 * « DPS nécessaire / DPS attendu / marge ». Tout est détecté à l'exécution :
 * sans `expectedThrow`, seul le mur s'affiche ; sans `enemyHpAt`, le mur est
 * recalculé depuis `core/economy`.
 */
function buildCalculatorSection(ctx: ScreenCtx): HTMLElement {
  const { game } = ctx;
  const details = section('Calculateur de dégâts', 'calculator');

  const body = el('div', 'debug-calc');
  body.setAttribute('data-role', 'calculator');
  details.appendChild(body);

  const actions = el('div', 'debug-actions');
  const replay = button('Rejouer le calcul');
  replay.setAttribute('data-action', 'calc-replay');
  actions.appendChild(replay);
  details.appendChild(actions);

  const render = (): void => {
    body.innerHTML = '';

    // ── Lancer attendu ────────────────────────────────────────────────
    const expected = expectedThrow(game);
    const throwList = el('dl', 'debug-calc-grid');
    throwList.setAttribute('data-role', 'calc-throw');
    if (expected) {
      throwList.append(
        calcRow('Dégâts moyens (sans sort)', formatDamage(expected.meanNoSpell), 'meanNoSpell'),
        calcRow('Dégâts moyens (avec critiques)', formatDamage(expected.meanWithCrit), 'meanWithCrit'),
        calcRow('Dégâts moyens (avec sorts)', formatDamage(expected.meanWithSpells), 'meanWithSpells'),
        calcRow('Min', formatDamage(expected.min), 'min'),
        calcRow('Max', formatDamage(expected.max), 'max'),
        calcRow('Chance de critique', formatPercent1(expected.critChance), 'critChance'),
        calcRow('Multiplicateur critique', formatMult(expected.critMultiplier), 'critMultiplier'),
        calcRow('Temps de retour', formatSeconds(expected.perThrowSeconds, 2), 'perThrowSeconds'),
        calcRow('DPS attendu', formatDamage(expected.dps), 'dps'),
      );
      // v0.5.0 : les sorts ne sont plus une liste fermée (sorts d'emplacement
      // **et** sorts de dés) — on rend ce que `expectedThrow()` renvoie.
      for (const [id, p] of Object.entries(expected.spellChance)) {
        throwList.appendChild(
          calcRow(`Probabilité — ${spellLabel(id)}`, formatPercent1(p), `spell-${id}`),
        );
      }
      // Grandeurs supplémentaires du cœur (v0.7.0 : Larbin, Premiers, ENFER,
      // Sorcellerie…) : affichées sous leur libellé connu, sinon leur nom brut.
      for (const [key, value] of Object.entries(expected.extra)) {
        throwList.appendChild(calcRow(factorLabel(key), formatNumber(value, 3), `extra-${key}`));
      }
      if (Object.keys(expected.spellChance).length === 0) {
        throwList.appendChild(calcRow('Probabilité — sorts', '—', 'spell-none'));
      }
    }
    body.appendChild(throwList);
    if (!expected) {
      body.appendChild(
        el('p', 'debug-hint', 'API core absente (game.expectedThrow) : seul le mur suivant est calculé.'),
      );
    }

    // ── Mur suivant ───────────────────────────────────────────────────
    body.appendChild(el('h4', 'debug-calc-title', 'Mur suivant (prochaine Invasion)'));
    const wall = nextWall(game);
    const wallList = el('dl', 'debug-calc-grid');
    wallList.setAttribute('data-role', 'calc-wall');
    wallList.append(
      calcRow("Niveau d'expédition", String(wall.level), 'level'),
      calcRow('PV moyen (ennemi ordinaire)', formatDamage(wall.meanEnemyHp), 'meanEnemyHp'),
      calcRow('Ennemis avant le boss', String(wall.enemyCount), 'enemyCount'),
      calcRow('PV du boss', formatDamage(wall.bossHp), 'bossHp'),
      calcRow('PV total', formatDamage(wall.totalHp), 'totalHp'),
      calcRow("Durée d'Invasion", formatSeconds(wall.duration, 0), 'duration'),
      calcRow('DPS nécessaire', formatDamage(wall.requiredDps), 'requiredDps'),
    );
    body.appendChild(wallList);

    // ── Verdict ───────────────────────────────────────────────────────
    const dps = expected?.dps ?? 0;
    const margin = wall.requiredDps > 0 ? dps / wall.requiredDps : 0;
    const verdict = el('p', 'debug-calc-verdict');
    verdict.setAttribute('data-role', 'calc-verdict');
    verdict.textContent =
      `DPS nécessaire ≈ ${formatDamage(wall.totalHp)} / ${formatNumber(wall.duration, 0)} s = ` +
      `${formatDamage(wall.requiredDps)} ; DPS attendu = ${expected ? formatDamage(dps) : '—'} ; ` +
      `marge ${expected ? formatMult(margin) : '—'}`;
    verdict.classList.toggle('debug-calc-verdict--ok', expected !== null && margin >= 1);
    verdict.classList.toggle('debug-calc-verdict--ko', expected !== null && margin < 1);
    body.appendChild(verdict);

    if (!hasEnemyHpAt(game)) {
      body.appendChild(
        el('p', 'debug-hint', 'enemyHpAt() absent du cœur : PV recalculés depuis core/economy.'),
      );
    }
  };

  replay.addEventListener('click', render);

  // Recalcul sur les événements qui changent la puissance du joueur. Chaque
  // écouteur se désabonne de lui-même dès que la section a quitté le document.
  for (const ev of ['purchase', 'gold'] as const) {
    const off = onGameEvent(game, ev, () => {
      if (!details.isConnected) {
        off();
        return;
      }
      render();
    });
    calcUnsubs.push(off);
  }

  render();
  return details;
}

// ────────────────────────────────────────────────── Section : outils rapides

function buildToolsSection(ctx: ScreenCtx, close: () => void, rerender: () => void): HTMLElement {
  const details = section('Outils rapides', 'tools');
  const actions = el('div', 'debug-actions dev-actions');

  const gold = button('+500 or');
  gold.setAttribute('data-dev', 'gold');
  gold.addEventListener('click', () => {
    const cheat = (window as unknown as { __dod?: { cheat?: { gold: (n: number) => void } } }).__dod
      ?.cheat;
    markTainted(ctx.game);
    cheat?.gold(ctx.game.state.gold + 500);
    rerender();
  });

  const skip = button('Terminer le combat en cours');
  skip.setAttribute('data-dev', 'skip');
  skip.addEventListener('click', () => {
    const g = ctx.game;
    if (g.state.phase === 'expedition' || g.state.phase === 'invasion') {
      markTainted(g);
      g.tick(g.state.timeRemaining + 0.01);
    }
    close();
  });

  const reset = button('Effacer la sauvegarde');
  reset.setAttribute('data-dev', 'reset');
  reset.addEventListener('click', () => {
    if (!window.confirm('Effacer la sauvegarde et recharger ?')) return;
    // Le journal d'achats de la télémétrie appartient à la partie, pas à l'installation.
    getTelemetry()?.resetPurchases();
    clearSave();
    window.location.reload();
  });

  // Info-bulles de première partie (phase 4, `ui/tips.ts`) : la préférence locale
  // « vues » est effacée, sans rechargement ; elles reviennent aux prochains
  // montages d'écran.
  const resetTips = button('Remettre les info-bulles à zéro');
  resetTips.setAttribute('data-dev', 'reset-tips');
  resetTips.addEventListener('click', () => {
    resetSeen();
    toast('Info-bulles remises à zéro.');
  });
  publishTipsDebug();

  actions.append(gold, skip, resetTips, reset);
  details.appendChild(actions);

  // « Tout l'arbre » (A98) : montre les nœuds que l'achat n'a pas encore dévoilés.
  // Préférence de la session ; elle ne change rien au jeu, donc ne marque pas la partie.
  const reveal = el('label', 'debug-check');
  const revealBox = el('input', 'f-check');
  revealBox.type = 'checkbox';
  revealBox.setAttribute('data-dev', 'tree-reveal');
  revealBox.checked = treeRevealAll();
  revealBox.addEventListener('change', () => setTreeRevealAll(revealBox.checked));
  reveal.append(revealBox, el('span', undefined, 'Tout l’arbre (nœuds non dévoilés compris)'));
  details.appendChild(reveal);

  details.appendChild(buildSpeedRow(ctx, rerender));
  return details;
}

/**
 * « Vitesse de combat » (A99) : ×1 / ×2 / ×4 / ×8 (boutons radio) et « Sans vol
 * de dés ». Préférences de l'installation, relues au lancement en mode debug ;
 * choisir un facteur ≠ ×1 ou cocher la case marque la partie (A87). Revenir à
 * ×1 ne la démarque pas.
 */
function buildSpeedRow(ctx: ScreenCtx, rerender: () => void): HTMLElement {
  const row = el('div', 'debug-choice debug-speed');
  row.setAttribute('data-role', 'speed-row');
  row.appendChild(el('span', 'debug-field-label', 'Vitesse de combat'));
  const group = el('div', 'debug-choice-actions debug-actions');
  group.setAttribute('role', 'radiogroup');
  group.setAttribute('aria-label', 'Vitesse de combat');
  const buttons: HTMLButtonElement[] = [];
  const sync = (): void => {
    for (const b of buttons) {
      const on = Number(b.dataset.speed) === currentSpeed();
      markPressed(b, on);
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on ? 0 : -1;
    }
  };
  for (const n of SPEEDS) {
    const b = button(`×${n}`);
    b.setAttribute('role', 'radio');
    b.setAttribute('data-dev', 'speed');
    b.dataset.speed = String(n);
    b.addEventListener('click', () => {
      if (n !== 1) markTainted(ctx.game);
      setSpeed(n);
      rerender();
    });
    buttons.push(b);
    group.appendChild(b);
  }
  sync();
  row.appendChild(group);

  const label = el('label', 'debug-check');
  const box = el('input', 'f-check');
  box.type = 'checkbox';
  box.setAttribute('data-dev', 'speed-noflight');
  box.checked = noFlight();
  box.addEventListener('change', () => {
    if (box.checked) markTainted(ctx.game);
    setNoFlight(box.checked);
    rerender();
  });
  label.append(box, el('span', undefined, 'Sans vol de dés (les dés se posent aussitôt)'));
  row.appendChild(label);

  // Le réglage peut aussi changer ailleurs (console de debug) : le groupe suit.
  const off = onSpeedChange(() => {
    if (!row.isConnected) {
      off();
      return;
    }
    sync();
    box.checked = noFlight();
  });
  calcUnsubs.push(off);
  return row;
}

// ─────────────────────────────────────────────── Section : télémétrie (phase 3)

/**
 * Section « Télémétrie » (phase 3, A78 à A80) : identifiant (début), consentement,
 * adresse effective, surcharge d'adresse (debug), file, dernier envoi, et les
 * actions « Envoyer maintenant », « Vider la file », « Copier le dernier
 * enregistrement ». Lit la façade `getTelemetry()` ; avant son installation
 * (quelques centaines de ms après le montage) la section l'indique.
 */
function buildTelemetrySection(rerender: () => void): HTMLElement {
  const details = section('Télémétrie', 'telemetry');
  const api = getTelemetry();
  if (!api) {
    details.appendChild(
      el('p', 'debug-hint', 'Télémétrie pas encore installée : rouvrez le panneau dans un instant.'),
    );
    return details;
  }
  const st = api.state();
  const row = (role: string, label: string, value: string): HTMLElement => {
    const p = el('p', 'debug-hint');
    p.append(`${label} : `);
    const b = el('b', undefined, value);
    b.setAttribute('data-dev', role);
    p.appendChild(b);
    return p;
  };
  const last =
    (st.lastAttemptAt ? `${st.lastAttemptAt.replace('T', ' ').slice(0, 19)} UTC · ${st.lastResult ?? '—'}` : (st.lastResult ?? 'aucun envoi')) +
    (st.lastDetail ? ` — ${st.lastDetail}` : '');
  details.append(
    row('telemetry-id', 'Identifiant', `${st.installId.slice(0, 8)}…`),
    row('telemetry-consent', 'Consentement', readConsent() ? 'actif' : 'retiré'),
    row('telemetry-url', 'Adresse effective', st.url ?? 'aucune'),
    row(
      'telemetry-queue',
      'File',
      `${st.queued} enregistrement${st.queued > 1 ? 's' : ''} · ${(st.bytes / 1024).toFixed(1)} Ko · ${st.dropped} jeté${st.dropped > 1 ? 's' : ''} · ${st.backend}`,
    ),
    row('telemetry-sent', 'Envoyés (session)', `${st.sent}${st.rejected > 0 ? ` · ${st.rejected} rejeté${st.rejected > 1 ? 's' : ''} (400/413, retirés de la file)` : ''}`),
    row('telemetry-last', 'Dernier envoi', last),
  );

  // Surcharge d'adresse : permet de tester un Worker depuis la préprod, avant de régler la variable de build.
  details.appendChild(el('p', 'debug-hint', 'Surcharge d’adresse (debug) — racine du Worker ou …/v1/reports :'));
  const input = el('input', 'f-input');
  input.type = 'text';
  input.placeholder = 'https://diceordie-telemetry.exemple.workers.dev';
  input.value = readPref(URL_OVERRIDE_KEY) ?? '';
  input.spellcheck = false;
  input.setAttribute('data-dev', 'telemetry-url-input');
  details.appendChild(input);
  const apply = button('Appliquer l’adresse');
  apply.setAttribute('data-dev', 'telemetry-url-apply');
  apply.addEventListener('click', () => {
    const v = input.value.trim();
    if (v) writePref(URL_OVERRIDE_KEY, v);
    else removePref(URL_OVERRIDE_KEY);
    toast(v ? 'Adresse enregistrée.' : 'Surcharge retirée.');
    rerender();
  });

  const send = button('Envoyer maintenant');
  send.setAttribute('data-dev', 'telemetry-send');
  send.addEventListener('click', () => {
    void api.flush().then((result) => {
      toast(`Envoi : ${result ?? 'rien à envoyer'}.`);
      if (details.isConnected) rerender();
    });
  });
  const clear = button('Vider la file');
  clear.setAttribute('data-dev', 'telemetry-clear');
  clear.addEventListener('click', () => {
    void api.clear().then(() => {
      toast('File vidée.');
      if (details.isConnected) rerender();
    });
  });
  const actions = el('div', 'debug-actions');
  actions.append(apply, send, clear);
  details.appendChild(actions);

  const record = api.lastRecord();
  details.appendChild(
    el('p', 'debug-hint', record ? 'Dernier enregistrement de cette session :' : 'Aucun enregistrement dans cette session.'),
  );
  if (record) {
    const block = exportBlock('diceordie-telemetry-record.json', JSON.stringify(record));
    block.area.setAttribute('data-dev', 'telemetry-record');
    details.appendChild(block.root);
  }
  return details;
}

// ───────────────────────────────────────────── Section : version complète (A112)

const REFUSAL_TEXT: Record<string, string> = {
  invalid: 'Code inconnu.',
  used: 'Code déjà utilisé sur un autre appareil.',
  rate: 'Trop d’essais, réessayez dans une heure.',
  unavailable: 'Vérification impossible (Worker indisponible ou sans adresse).',
  network: 'Vérification impossible (hors ligne ?).',
};

/**
 * Section « Version complète » (A112) : état de l'appareil, « Simuler l'achat »
 * (source `debug`, **marque la partie**), « Retirer », saisie d'un code (même
 * client que l'écran d'achat) et file des événements du mur. Masquée sans l'API
 * du cœur (avant la fusion du lot A112).
 */
function buildEntitlementSection(ctx: ScreenCtx, rerender: () => void): HTMLElement | null {
  const { game } = ctx;
  if (!hasGateApi(game)) return null;
  const details = section('Version complète', 'entitlement');
  const api = getEntitlement();
  const stored = readEntitlement();
  const state = el(
    'p',
    'debug-hint',
    stored ? `Débloqué (${stored.source}) depuis ${stored.at.replace('T', ' ').slice(0, 19)} UTC` : 'Essai',
  );
  state.setAttribute('data-dev', 'entitlement-state');
  details.appendChild(state);

  const entitle = button('Simuler l’achat');
  entitle.setAttribute('data-dev', 'entitle');
  entitle.disabled = !api;
  entitle.addEventListener('click', () => {
    api?.unlockWith('debug');
    toast('Jeu complet simulé : partie marquée debug.');
    if (entitle.isConnected) rerender();
  });
  const revoke = button('Retirer');
  revoke.setAttribute('data-dev', 'revoke');
  revoke.disabled = !api;
  revoke.addEventListener('click', () => {
    game.markTainted();
    api?.revokeEntitlement();
    toast('Jeu complet retiré.');
    if (revoke.isConnected) rerender();
  });
  const actions = el('div', 'debug-actions');
  actions.append(entitle, revoke);
  details.appendChild(actions);

  details.appendChild(el('p', 'debug-hint', 'Code de déblocage (même client que l’écran d’achat) :'));
  const input = el('input', 'f-input');
  input.type = 'text';
  input.placeholder = 'XXXX-XXXX-XXXX';
  input.spellcheck = false;
  input.autocomplete = 'off';
  input.setAttribute('data-dev', 'entitlement-code');
  input.addEventListener('input', () => {
    const f = formatCodeInput(input.value);
    if (f !== input.value) input.value = f;
  });
  details.appendChild(input);
  const redeem = button('Valider le code');
  redeem.setAttribute('data-dev', 'entitlement-redeem');
  redeem.disabled = !api;
  const result = el('p', 'debug-hint');
  result.setAttribute('data-dev', 'entitlement-code-result');
  redeem.addEventListener('click', () => {
    redeem.disabled = true;
    void redeemUnlockCode(input.value, { installId: getInstallId(), base: currentWorkerBase() }).then((out) => {
      redeem.disabled = false;
      if (out.ok) {
        api?.unlockWith('code', out.token);
        toast('Code accepté : jeu complet débloqué.');
        if (details.isConnected) rerender();
      } else {
        result.textContent = REFUSAL_TEXT[out.reason] ?? out.reason;
      }
    });
  });
  details.append(redeem, result);

  const pending = pendingGateEvents();
  const events = el('p', 'debug-hint');
  events.append('Événements du mur en attente : ');
  const count = el('b', undefined, String(pending));
  count.setAttribute('data-dev', 'events-pending');
  events.appendChild(count);
  details.appendChild(events);
  const flush = button('Envoyer');
  flush.setAttribute('data-dev', 'events-flush');
  flush.addEventListener('click', () => {
    void flushGateEvents().then((out) => {
      toast(`Événements du mur : ${out.result}${out.sent > 0 ? ` (${out.sent} envoyé${out.sent > 1 ? 's' : ''})` : ''}.`);
      if (details.isConnected) rerender();
    });
  });
  details.appendChild(flush);

  // A118 : l'acquittement Google Play (file d'une entrée, relances du Worker).
  const waiting = readAck();
  const ackLine = el(
    'p',
    'debug-hint',
    waiting
      ? `Acquittement Play : en attente (${waiting.attempts} essai${waiting.attempts > 1 ? 's' : ''}, prochain à ${new Date(waiting.nextAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})`
      : 'Acquittement Play : aucun en attente',
  );
  ackLine.setAttribute('data-dev', 'ack-state');
  details.appendChild(ackLine);
  return details;
}

// ─────────────────────────────────────────────────── Section : vue de combat

const VIEW_LABEL: Record<string, string> = { '3d': '3D low-poly', stub: 'secours (sans WebGL2)' };

/**
 * Bloc « Vue de combat » : dit quelle vue est montée. Depuis la v0.22.0 (A58)
 * il n'y a plus de choix — la vue 2D PixiJS et sa bascule (préférence
 * `diceordie.view`) ont disparu : la 3D low-poly, ou la vue de secours sans
 * WebGL2 (ou si le pipeline 3D est refusé par le pilote).
 */
function buildViewSection(): HTMLElement {
  const details = section('Vue de combat', 'view3d');

  const active =
    (window as unknown as { __dod?: { viewKind?: string } }).__dod?.viewKind ?? null;
  details.appendChild(
    el(
      'p',
      'debug-hint',
      `Vue active : ${active ? (VIEW_LABEL[active] ?? active) : 'inconnue (rechargez en mode debug)'}.`,
    ),
  );
  details.appendChild(
    el(
      'p',
      'debug-hint',
      'La 3D low-poly est l’unique rendu ; sans WebGL2, une vue de secours sobre ' +
        'prend le relais (A58). Les réglages de rendu sont dans « Rendu 3D », ' +
        'appliqués sans rechargement.',
    ),
  );
  return details;
}

// ────────────────────────────────────────── Section : rendu 3D (v0.10.1)

/**
 * Réglages de rendu de la vue 3D, **appliqués en direct** : le GD cherche des
 * valeurs lisibles (le rendu livré lui paraît trop pixélisé) et nous renvoie
 * le JSON, qui deviendra ensuite le défaut du code.
 *
 * Détection à l'exécution, comme toutes les autres sections : sans
 * `setRenderParams` sur la vue montée (vue de secours), le bloc n'est pas
 * construit du tout.
 */
interface RenderApi3d {
  get(): RenderParams;
  set(patch: Partial<RenderParams>): RenderParams;
  reset(): RenderParams;
}

/** Vue 3D telle que la voit le panneau : trois méthodes, toutes optionnelles. */
type MaybeRenderView = Partial<{
  setRenderParams: (patch: Partial<RenderParams>) => RenderParams;
  getRenderParams: () => RenderParams;
  resetRenderParams: () => RenderParams;
}>;

function renderApi3d(ctx: ScreenCtx): RenderApi3d | null {
  const view = ctx.battle as unknown as MaybeRenderView;
  const set = view.setRenderParams;
  const get = view.getRenderParams;
  if (typeof set === 'function' && typeof get === 'function') {
    const reset = view.resetRenderParams;
    return {
      get: () => get.call(view),
      set: (patch) => set.call(view, patch),
      reset: () =>
        typeof reset === 'function'
          ? reset.call(view)
          : set.call(view, { ...DEFAULT_RENDER_PARAMS }),
    };
  }
  // Repli : l'API de debug de la vue (`window.__dod.view3d`), posée au montage.
  const api = (window as unknown as { __dod?: { view3d?: Partial<RenderApi3d> } }).__dod?.view3d;
  if (api && typeof api.set === 'function' && typeof api.get === 'function') {
    const dget = api.get;
    const dset = api.set;
    const dreset = api.reset;
    return {
      get: () => dget.call(api),
      set: (patch) => dset.call(api, patch),
      reset: () =>
        typeof dreset === 'function' ? dreset.call(api) : dset.call(api, { ...DEFAULT_RENDER_PARAMS }),
    };
  }
  return null;
}

/** Nombre de décimales à afficher pour un pas de curseur donné. */
function stepDecimals(step: number): number {
  return step < 0.1 ? 2 : 1;
}

/** Un choix discret : un bouton par valeur, l'actif en `f-btn--primary` (`aria-pressed`). */
interface ChoiceSpec {
  key: keyof RenderParams;
  label: string;
  values: Array<{ value: number | boolean | string; label: string }>;
}

const YES_NO = [
  { value: true, label: 'oui' },
  { value: false, label: 'non' },
];

/** Choix du rendu (v0.11.0) : en tête du bloc. */
const LOWPOLY_CHOICES: readonly ChoiceSpec[] = [
  {
    key: 'resolution',
    label: 'Définition',
    values: [
      { value: 'native', label: 'native' },
      { value: 'x2', label: '≤ ×2' },
      { value: 'x1_5', label: '≤ ×1,5' },
    ],
  },
  { key: 'shadows', label: 'Ombres', values: YES_NO },
  { key: 'raritySheen', label: 'Reflet de rareté', values: YES_NO },
  // A78 : nuage de particules des dés Maîtres (aucune passe à « non »).
  { key: 'diceParticles', label: 'Particules des dés', values: YES_NO },
  {
    key: 'diceMaterial',
    label: 'Matière des dés',
    values: [
      { value: 'glossy', label: 'brillante' },
      { value: 'matte', label: 'mate' },
    ],
  },
  {
    // A67 : arrondi des dés à facettes plates (low-poly, défaut) ou lissé (v0.25.0).
    key: 'diceShading',
    label: 'Ombrage des dés',
    values: [
      { value: 'faceted', label: 'facetté' },
      { value: 'smooth', label: 'lissé' },
    ],
  },
  {
    key: 'timeOfDay',
    label: 'Heure',
    values: [
      { value: 'auto', label: 'auto (jour/nuit au sort par expédition)' },
      { value: 'morning', label: 'matin' },
      { value: 'day', label: 'jour' },
      { value: 'dusk', label: 'crépuscule' },
      { value: 'night', label: 'nuit' },
    ],
  },
];

/** Patch d'une clé : le type indexé se perd à la construction, on le rétablit. */
function renderPatch(key: keyof RenderParams, value: number | boolean | string): Partial<RenderParams> {
  return { [key]: value } as Partial<RenderParams>;
}

function buildRender3dSection(ctx: ScreenCtx): HTMLElement | null {
  const api = renderApi3d(ctx);
  if (!api) return null;
  const details = section('Rendu 3D', 'render3d');
  details.appendChild(
    el(
      'p',
      'debug-hint',
      'Réglages appliqués immédiatement (aucun rechargement) et conservés dans ' +
        '« diceordie.view3d.params ». Exportez le JSON pour nous envoyer vos valeurs.',
    ),
  );

  // Tout l'affichage se resynchronise sur les valeurs **renvoyées** par la
  // vue : c'est elle qui borne et cale sur le pas (`normalizeRenderParams`).
  const syncers: Array<(p: RenderParams) => void> = [];
  const json = exportBlock('diceordie-render3d.json', '{}');
  const apply = (patch: Partial<RenderParams>): void => {
    const next = api.set(patch);
    for (const s of syncers) s(next);
    json.setValue(JSON.stringify(next, null, 2));
  };

  const sliderRow = (field: RenderSlider): HTMLElement => {
    const row = el('label', 'debug-field debug-field--range');
    const name = el('span', 'debug-field-label', field.label);
    const input = el('input', 'f-range debug-range');
    input.type = 'range';
    input.min = String(field.min);
    input.max = String(field.max);
    input.step = String(field.step);
    input.setAttribute('data-param', field.key);
    const value = el('span', 'debug-range-value');
    value.setAttribute('data-param-value', field.key);
    input.addEventListener('input', () => apply(renderPatch(field.key, Number(input.value))));
    syncers.push((p) => {
      input.value = String(p[field.key]);
      value.textContent = formatNumber(p[field.key], stepDecimals(field.step));
    });
    row.append(name, input, value);
    return row;
  };
  const sliders = (keys: (k: keyof RenderParams) => boolean): HTMLElement => {
    const form = el('div', 'debug-form');
    for (const field of RENDER_SLIDERS) if (keys(field.key)) form.appendChild(sliderRow(field));
    return form;
  };
  const choiceRow = (choice: ChoiceSpec): HTMLElement => {
    const row = el('div', 'debug-choice');
    row.append(el('span', 'debug-field-label', choice.label));
    const actions = el('div', 'debug-actions debug-choice-actions');
    for (const option of choice.values) {
      const b = button(option.label, 'f-btn f-btn--sm');
      b.setAttribute('data-param', choice.key);
      b.setAttribute('data-value', String(option.value));
      b.addEventListener('click', () => apply(renderPatch(choice.key, option.value)));
      syncers.push((p) => {
        markPressed(b, p[choice.key] === option.value);
      });
      actions.appendChild(b);
    }
    row.appendChild(actions);
    return row;
  };

  // ── Définition, ombres, reflet, heure, contour ───────────────────────
  for (const choice of LOWPOLY_CHOICES) details.appendChild(choiceRow(choice));
  details.appendChild(sliders((k) => k === 'monsterOutline'));
  const lowpolyActions = el('div', 'debug-actions');
  // « Aperçu low-poly » : le rendu des aperçus validés par le GD, en un clic —
  // c'est aussi le défaut livré (v0.11.0).
  const apercuLp = button('Aperçu low-poly', 'f-btn f-btn--sm');
  apercuLp.setAttribute('data-action', 'render3d-preset-lowpoly');
  apercuLp.addEventListener('click', () => {
    apply({ ...PRESET_APERCU });
    toast('Rendu 3D : préréglage « Aperçu low-poly » appliqué.');
  });
  lowpolyActions.appendChild(apercuLp);
  details.appendChild(lowpolyActions);

  // ── Dés et caméra ────────────────────────────────────────────────────
  details.appendChild(el('p', 'debug-hint', 'Dés et caméra :'));
  details.appendChild(sliders((k) => k !== 'monsterOutline'));

  // ── Réinitialiser / Exporter ─────────────────────────────────────────
  const actions = el('div', 'debug-actions');
  const reset = button('Réinitialiser');
  reset.setAttribute('data-action', 'render3d-reset');
  reset.addEventListener('click', () => {
    const next = api.reset();
    for (const s of syncers) s(next);
    json.setValue(JSON.stringify(next, null, 2));
    toast('Rendu 3D : valeurs livrées rétablies.');
  });
  const exportBtn = button('Exporter JSON', 'f-btn f-btn--primary f-btn--sm');
  exportBtn.setAttribute('data-action', 'render3d-export');
  exportBtn.addEventListener('click', () => {
    json.setValue(JSON.stringify(api.get(), null, 2));
    copyText(json.area);
  });
  actions.append(reset, exportBtn);
  details.append(actions, json.root);

  const current = api.get();
  for (const s of syncers) s(current);
  json.setValue(JSON.stringify(current, null, 2));
  return details;
}

// ───────────────────────────────────────────────────────────────── Assemblage

/** Tableau récapitulatif de l'état courant (inchangé depuis la v0.2.1). */
function buildInfoTable(ctx: ScreenCtx, version: string, debug: boolean): HTMLElement {
  const s = ctx.game.state;
  const stats = ctx.game.getDerivedStats();
  const rows: [string, string][] = [
    ['Version', version],
    ['Phase', s.phase],
    ['Niveau / jour / vague', `${s.level} / ${s.day} / ${s.wave}`],
    ['Or', `${s.gold} (+${s.pendingGold} en attente)`],
    ['Runs / meilleur niveau', `${s.stats.runs} / ${s.stats.bestLevel}`],
    ['Lancers / ennemis tués', `${s.stats.throws} / ${s.stats.totalKills}`],
    [
      'Dégâts / critique / temps de retour',
      `+${Math.round((stats.damageMult - 1) * 100)} % / ${Math.round(stats.critChance * 100)} % / ${stats.throwCooldown.toFixed(2)} s`,
    ],
    ['Emplacements / dés en inventaire', `${stats.slots} / ${s.inventory.length}`],
    [
      'Puissance des sorts / Préparation',
      `×${stats.spellPower.toFixed(2)} / +${Math.round((prepBoost(ctx.game) ?? 0) * 100)} %`,
    ],
    ['Mode debug', debug ? 'actif' : 'inactif (ajouter ?debug à l’URL)'],
  ];
  const wrap = el('div', 'md-table-wrap');
  let html = '<table class="md-table"><tbody>';
  for (const [k, v] of rows) html += `<tr><th>${k}</th><td>${v}</td></tr>`;
  wrap.innerHTML = html + '</tbody></table>';
  return wrap;
}

/**
 * Remplit le corps de l'onglet Debug. `close` ferme la modale,
 * `rerender` la redessine (après une action qui change l'état).
 */
export function renderDebugTab(
  body: HTMLElement,
  ctx: ScreenCtx,
  opts: { version: string; debug: boolean; close: () => void; rerender: () => void },
): void {
  for (const off of calcUnsubs) off();
  calcUnsubs = [];
  body.innerHTML = '';
  body.appendChild(buildInfoTable(ctx, opts.version, opts.debug));
  // Phase 7 (A87) : une partie marquée par une action de debug sort des classements.
  if (isTainted(ctx.game)) {
    const notice = el('p', 'f-notice debug-tainted');
    notice.setAttribute('data-role', 'debug-tainted');
    notice.setAttribute('role', 'status');
    notice.append(el('span', undefined, 'Partie marquée debug : exclue des classements'));
    body.appendChild(notice);
  }
  if (!opts.debug) return;

  const panel = el('div', 'debug-panel');
  panel.append(
    buildToolsSection(ctx, opts.close, opts.rerender),
    buildViewSection(),
    buildTelemetrySection(opts.rerender),
  );
  const render3d = buildRender3dSection(ctx);
  if (render3d) panel.appendChild(render3d);
  panel.append(
    buildCalculatorSection(ctx),
    buildBalanceSection(ctx, opts.rerender),
    buildSaveSection(ctx),
  );
  const entitlement = buildEntitlementSection(ctx, opts.rerender);
  if (entitlement) panel.appendChild(entitlement);
  panel.appendChild(buildHistorySection(opts.rerender));
  const auto = buildAutoThrowSection(ctx);
  if (auto) panel.appendChild(auto);
  const forge = buildForgeSection(ctx, opts.rerender);
  if (forge) panel.appendChild(forge);
  body.appendChild(panel);
}
